import { useState } from 'react';
import { Outlet, Link, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LICENSE_VERIFICATION_BADGE, MARKETPLACE_TAGLINE } from '@lighthouse/shared';
import { useAuthStore } from '../hooks/useAuthStore';
import { logoutSession } from '../services/api';
import { Avatar } from './Avatar';
import { NotificationBell } from './NotificationBell';
import { ThemeToggle } from './ThemeToggle';
import { FooterSocialLinks } from './FooterSocialLinks';
import { AppStoreBadgesFooter } from './landing/AppStoreBadges';
import { CrisisBanner } from './CrisisBanner';
import { BrandMark } from './BrandMark';
import { therapistNeedsOnboarding, useTherapistSetup } from '../hooks/useTherapistSetup';

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink to={to} className={({ isActive }) => `site-nav-link ${isActive ? 'active' : ''}`}>
      {children}
    </NavLink>
  );
}

export function Layout() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { data: therapistSetup } = useTherapistSetup();
  const showTherapistOnboarding = therapistNeedsOnboarding(therapistSetup);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);

  const handleLogout = () => {
    void logoutSession();
    navigate('/');
    setDropOpen(false);
  };

  const dashPath =
    user?.role === 'therapist'
      ? '/therapist/dashboard'
      : user?.role === 'corporate_hr'
        ? '/corporate/dashboard'
        : '/dashboard';

  const settingsPath = user?.role === 'therapist' ? '/therapist/settings' : '/profile';

  return (
    <div className="app-shell flex min-h-screen flex-col">
      <nav className="site-header glass-nav sticky top-0 z-40">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-6 px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="MyLighthouse home">
            <BrandMark variant="symbol-dark" className="hidden h-8 sm:block" />
            <span className="font-display text-xl font-bold tracking-tight">
              <span className="text-white">My</span>
              <span className="text-[var(--color-brand-primary)]">Lighthouse</span>
            </span>
          </Link>

          {/* Desktop account controls */}
          <div className="hidden items-center gap-2 md:flex">
            <ThemeToggle compact />
            {user ? (
              <>
                <NotificationBell />
                <div className="relative ml-1 border-l border-gray-200/80 pl-2">
                  <button
                    onClick={() => setDropOpen((d) => !d)}
                    className="flex items-center gap-1.5 rounded-full py-1 pl-1 pr-1 transition-colors hover:bg-white/70"
                    aria-label="Account menu"
                    aria-expanded={dropOpen}
                  >
                    <Avatar
                      name={[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}
                      src={user.role === 'therapist' ? therapistSetup?.profilePhotoUrl : null}
                      size="sm"
                    />
                    <svg
                      className="h-4 w-4 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {dropOpen && (
                    <div className="glass-dropdown absolute right-0 z-50 mt-2 w-56" role="menu">
                      <p className="truncate border-b border-gray-100 px-4 py-2.5 text-xs text-gray-500">
                        {user.email}
                      </p>
                      <Link
                        to={dashPath}
                        onClick={() => setDropOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        role="menuitem"
                      >
                        📊 Dashboard
                      </Link>
                      {user.role === 'client' && (
                        <>
                          <Link
                            to="/progress"
                            onClick={() => setDropOpen(false)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                            role="menuitem"
                          >
                            📈 Progress
                          </Link>
                          <Link
                            to="/tools"
                            onClick={() => setDropOpen(false)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                            role="menuitem"
                          >
                            🧰 Tools
                          </Link>
                          <Link
                            to="/learn"
                            onClick={() => setDropOpen(false)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                            role="menuitem"
                          >
                            📚 Learn
                          </Link>
                          <Link
                            to="/referrals"
                            onClick={() => setDropOpen(false)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                            role="menuitem"
                          >
                            🎁 Referrals
                          </Link>
                        </>
                      )}
                      {user.role === 'therapist' && (
                        <>
                          <Link
                            to="/therapist/profile"
                            onClick={() => setDropOpen(false)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                            role="menuitem"
                          >
                            ✏️ Public profile
                          </Link>
                          <Link
                            to="/therapist/availability"
                            onClick={() => setDropOpen(false)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                            role="menuitem"
                          >
                            📅 Availability
                          </Link>
                          <Link
                            to="/therapist/earnings"
                            onClick={() => setDropOpen(false)}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                            role="menuitem"
                          >
                            💰 Earnings
                          </Link>
                          {showTherapistOnboarding ? (
                            <Link
                              to="/therapist/onboarding"
                              onClick={() => setDropOpen(false)}
                              className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                              role="menuitem"
                            >
                              📋 Onboarding
                            </Link>
                          ) : null}
                        </>
                      )}
                      <Link
                        to={settingsPath}
                        onClick={() => setDropOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        role="menuitem"
                      >
                        ⚙️ Settings
                      </Link>
                      <div className="my-1 border-t border-gray-100" />
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                        role="menuitem"
                      >
                        ← Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="btn-ghost text-sm text-white hover:bg-white/10 hover:text-white"
                >
                  Log in
                </Link>
                <Link to="/register" className="btn-primary text-sm">
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile actions */}
          <div className="flex items-center gap-2 md:hidden">
            <ThemeToggle compact />
            {user && <NotificationBell />}
            <button
              className="rounded-lg p-2 text-white/80 hover:bg-white/10"
              onClick={() => setMenuOpen((m) => !m)}
              aria-label="Toggle menu"
            >
              {menuOpen ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="hidden border-t border-white/40 md:block">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-1 px-4 py-2 sm:px-6 lg:px-8">
            {user?.role === 'therapist' ? (
              <>
                <NavItem to="/therapist/dashboard">Dashboard</NavItem>
                <NavItem to="/therapist/availability">Availability</NavItem>
                <NavItem to="/therapist/earnings">Earnings</NavItem>
              </>
            ) : (
              <>
                <NavItem to="/therapists">Find a Therapist</NavItem>
                <NavItem to="/assessments">Assessments</NavItem>
                <NavItem to="/organizations">For Organizations</NavItem>
                <NavItem to="/learn">Resources</NavItem>
                <NavItem to="/about">About</NavItem>
                {user?.role === 'client' && (
                  <NavItem to="/assessment">Mental Health Check-In</NavItem>
                )}
                {user?.role === 'client' && <NavItem to="/tools">Tools</NavItem>}
              </>
            )}
          </div>
        </div>

        {user?.role === 'therapist' ? null : <CrisisBanner />}

        {/* Mobile menu */}
        {menuOpen && (
          <div
            className="glass-nav space-y-1 border-t border-white/40 px-4 py-3 md:hidden"
            role="navigation"
            aria-label="Mobile navigation"
          >
            {user?.role === 'therapist' ? (
              <>
                <Link
                  to="/therapist/dashboard"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-white/85"
                >
                  Dashboard
                </Link>
                <Link
                  to="/therapist/profile"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-white/85"
                >
                  Public profile
                </Link>
                <Link
                  to="/therapist/availability"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-white/85"
                >
                  Availability
                </Link>
                <Link
                  to="/therapist/earnings"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-white/85"
                >
                  Earnings
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/therapists"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-white/85"
                >
                  Find a Therapist
                </Link>
                <Link
                  to="/assessments"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-white/85"
                >
                  Assessments
                </Link>
                <Link
                  to="/organizations"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-white/85"
                >
                  For Organizations
                </Link>
                <Link
                  to="/learn"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-white/85"
                >
                  Resources
                </Link>
                <Link
                  to="/about"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-white/85"
                >
                  About
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-white/85"
                >
                  Contact
                </Link>
                {user?.role === 'client' && (
                  <Link
                    to="/tools"
                    onClick={() => setMenuOpen(false)}
                    className="block py-2 text-sm font-medium text-white/85"
                  >
                    Tools
                  </Link>
                )}
              </>
            )}
            {user ? (
              <>
                <div className="my-2 border-t border-gray-100" />
                <Link
                  to={dashPath}
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-white/85"
                >
                  Dashboard
                </Link>
                <Link
                  to={settingsPath}
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-white/85"
                >
                  Settings
                </Link>
                <button
                  onClick={handleLogout}
                  className="block py-2 text-sm font-medium text-red-600"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <div className="my-2 border-t border-gray-100" />
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-white"
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-white"
                >
                  Get Started
                </Link>
              </>
            )}
            <a href="/crisis.html" className="block py-2 text-sm font-medium text-red-600">
              🆘 Crisis Support
            </a>
          </div>
        )}
      </nav>

      {/* Click-outside to close dropdown */}
      {dropOpen && (
        <div className="fixed inset-0 z-30" onClick={() => setDropOpen(false)} aria-hidden="true" />
      )}

      <main className="min-w-0 flex-1" id="main-content">
        <Outlet />
      </main>

      <footer className="site-footer border-t border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-8 grid gap-8 md:grid-cols-4">
            <div>
              <p className="text-lg font-bold text-white">MyLighthouse</p>
              <p className="mt-2 text-xs text-white/70">{MARKETPLACE_TAGLINE}.</p>
              <p className="mt-2 text-[11px] text-white/55">Built by StackMap Systems</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="badge-green text-[10px]">{LICENSE_VERIFICATION_BADGE}</span>
                <span className="badge-blue text-[10px]">NDPA Compliant</span>
              </div>
              <FooterSocialLinks />
              <AppStoreBadgesFooter />
            </div>
            <div>
              <p className="mb-3 text-sm font-semibold text-white">Platform</p>
              <div className="space-y-2">
                <Link to="/therapists" className="block text-sm text-white/70 hover:text-white">
                  Find a Therapist
                </Link>
                <Link to="/groups" className="block text-sm text-white/70 hover:text-white">
                  Group Sessions
                </Link>
                <Link to="/learn" className="block text-sm text-white/70 hover:text-white">
                  Learn
                </Link>
                <Link to="/assessment" className="block text-sm text-white/70 hover:text-white">
                  Free Assessment
                </Link>
                <Link to="/help" className="block text-sm text-white/70 hover:text-white">
                  Help
                </Link>
                <Link to="/about" className="block text-sm text-white/70 hover:text-white">
                  About
                </Link>
                <Link to="/contact" className="block text-sm text-white/70 hover:text-white">
                  Contact us
                </Link>
              </div>
            </div>
            <div>
              <p className="mb-3 text-sm font-semibold text-white">For Business</p>
              <div className="space-y-2">
                <Link
                  to="/register/corporate"
                  className="block text-sm text-white/70 hover:text-white"
                >
                  Corporate EAP
                </Link>
                <Link
                  to="/corporate/trainings"
                  className="block text-sm text-white/70 hover:text-white"
                >
                  Corporate trainings
                </Link>
                <Link to="/hmo-partners" className="block text-sm text-white/70 hover:text-white">
                  HMO Partners
                </Link>
                <a href="#campus" className="block text-sm text-white/70 hover:text-white">
                  Campus Programme
                </a>
              </div>
            </div>
            <div>
              <p className="mb-3 text-sm font-semibold text-white">Support</p>
              <div className="space-y-2">
                <a
                  href="/crisis.html"
                  className="block text-sm font-medium text-red-500 hover:text-red-700"
                >
                  🆘 Crisis Hotlines
                </a>
                <Link to="/privacy" className="block text-sm text-white/70 hover:text-white">
                  {t('footer.privacy')}
                </Link>
                <Link to="/terms" className="block text-sm text-white/70 hover:text-white">
                  {t('footer.terms')}
                </Link>
                <a
                  href="mailto:hi@mylighthouse.ng"
                  className="block text-sm text-white/70 hover:text-white"
                >
                  hi@mylighthouse.ng
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
